import type { SelectHTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";

type SelectProps = {
  readonly label: string;
  readonly options: readonly string[];
  readonly placeholder?: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly className?: string;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, "children">;

export const Select = ({
  label,
  options,
  placeholder = "Select an option",
  error,
  required = false,
  className,
  id,
  ...rest
}: SelectProps) => {
  const selectId = id ?? (label ? label.toLowerCase().replaceAll(/\s+/g, "-") : "select");

  return (
    <div className={twMerge("space-y-1.5", className)}>
      {label && (
        <label htmlFor={selectId} className="form-label">
          {label}
          {required && <span className="form-label-asterisk">*</span>}
        </label>
      )}
      <select
        id={selectId}
        className={twMerge(
          "w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-all duration-200 ease-in-out focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1",
          error ? "border-red-400" : "border-gray-300"
        )}
        {...rest}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};
